import { z } from 'zod';

/**
 * 利用企業編集フォームのバリデーションスキーマを生成する（新版）
 *
 * PEN-742 で対象の 12 項目 + ギフトプラン / グループ / カスタムフィールド / 機能フラグを含む。
 * PEN-897 で仕様の項目表に合わせて、以下 10 項目の必須チェックを除去し任意項目化した。
 * PEN-896 で仕様の項目表に合わせて、電話番号（必須）・お電話がつながりやすい時間帯（任意）を追加した。
 * PEN-895 で仕様の項目表に合わせて、会社名・法人番号・外部企業ID・利用予定者数・担当者氏名・
 * メールアドレスを追加した（PEN-742 の対象範囲が LEA-1908 由来の 13 項目に限定されていたため未実装だった）。
 * PEN-900 で、取引店舗の選択肢が 0 件のとき項目自体が非表示になる仕様に合わせて、
 * `isPartnerBranchRequired` を受け取るファクトリ関数化した。選択肢が 1 件以上ある場合のみ
 * partnerBranchId を必須とする（0 件のときは項目が非表示になるためバリデーション対象外）。
 * グループ（groupId）は本チケットのスコープ外のため、常に任意のまま変更しない。
 * バリデーションは CreateClientForm の同名フィールドを踏襲する。
 *
 * @param isPartnerBranchRequired - 取引店舗選択肢が 1 件以上存在するか（true のとき partnerBranchId を必須にする）
 *
 * @property name - 会社名（必須）
 * @property nameKana - 会社名（フリガナ）（任意）
 * @property representativeName - 代表者氏名（任意）
 * @property corporateNumber - 法人番号（任意）
 * @property externalClientId - 外部企業ID（任意）
 * @property contactPersonName - 担当者氏名（必須）
 * @property contactPersonNameKana - 担当者氏名（フリガナ）（必須）
 * @property contactPersonEmail - メールアドレス（必須）
 * @property contactPersonPhone - 担当者電話番号（必須。数字とハイフンのみ）
 * @property contactPreferredTime - お電話がつながりやすい時間帯（平日）（任意）
 * @property postalCode - 郵便番号（任意。入力する場合はハイフンなし7桁の数字）
 * @property prefectureCode - 都道府県コード（任意）
 * @property city - 市区町村（任意）
 * @property streetAddress - 番地以降（任意）
 * @property employeeCount - 利用予定者数（必須、0以上の整数）
 * @property billingContactName - 請求担当者氏名（任意）
 * @property billingContactNameKana - 請求担当者氏名（フリガナ）（任意）
 * @property billingEmail - 請求書送付先メールアドレス（任意。入力する場合はメール形式）
 * @property partnerBranchId - 取引店舗ID（`isPartnerBranchRequired` が true のときのみ必須）
 * @property groupId - グループID（任意、単一選択）
 * @property clientFeatureFlags - クライアント機能フラグ（任意）
 * @property customFields - カスタムフィールド（任意）
 */
export const editClientSchema = (params: { isPartnerBranchRequired: boolean }) =>
  z.object({
    // 会社情報（編集対象）
    name: z.string().trim().min(1, '会社名を入力してください。'),
    nameKana: z.string().trim(),
    representativeName: z.string().trim(),
    corporateNumber: z.string().optional(),
    externalClientId: z.string().optional(),
    postalCode: z
      .string()
      .trim()
      .refine(value => value === '' || /^\d{7}$/.test(value), {
        message: '郵便番号はハイフンなし7桁の数字で入力してください。',
      }),
    prefectureCode: z.string(),
    city: z.string().trim(),
    streetAddress: z.string().trim(),
    employeeCount: z.preprocess(
      val => (typeof val === 'string' && val.trim() === '' ? Number.NaN : val),
      z.coerce
        .number('半角数字で入力してください')
        .int('半角数字で入力してください')
        .gte(0, '0以上の整数を入力してください。')
    ),
    partnerBranchId: z
      .string()
      .optional()
      .refine(value => !params.isPartnerBranchRequired || !!value, {
        message: '取引店舗を選択してください。',
      }),
    // プラン情報
    clientFeatureFlags: z.array(z.string()),
    // 担当者情報（編集対象）
    contactPersonName: z.string().trim().min(1, '担当者氏名を入力してください。'),
    contactPersonNameKana: z.string().trim().min(1, '担当者氏名（フリガナ）を入力してください。'),
    contactPersonEmail: z.email({ message: 'メールアドレスの形式が正しくありません。' }),
    contactPersonPhone: z
      .string()
      .trim()
      .min(1, '電話番号を入力してください。')
      .regex(/^[\d-]+$/, '電話番号は数字とハイフンのみ入力してください。'),
    contactPreferredTime: z.string().trim(),
    // 請求先情報（編集対象）
    billingContactName: z.string().trim(),
    billingContactNameKana: z.string().trim(),
    billingEmail: z
      .string()
      .trim()
      .refine(value => value === '' || z.email().safeParse(value).success, {
        message: '請求書送付先メールアドレスの形式が正しくありません。',
      }),
    // グループ / カスタムフィールド（LegacyEditClient から引き継ぎ）
    groupId: z.string().optional(),
    customFields: z
      .record(z.string(), z.union([z.string(), z.array(z.string())]).optional())
      .optional(),
  });

/**
 * 利用企業編集フォームのフィールド値の型（新版）
 */
export type EditClientFormFieldValues = z.infer<ReturnType<typeof editClientSchema>>;
